import React, { useEffect, useState } from "react";
import "./MyOrder.css";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router";
import ReceiptIcon from "@mui/icons-material/Receipt";

import {
  getAllOrderAsync,
  selectAllOrders,
} from "../../features/cartPage/OrderSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import "react-lazy-load-image-component/src/effects/blur.css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { formatWithCommasWithDecimal } from "../../utils/utils";

function MyOrder() {
  const dispatch = useDispatch();
  const orders = useSelector(selectAllOrders);
  const userDetail = useSelector(selectUser);
  const navigate = useNavigate();
  const [showAllProducts, setShowAllProducts] = useState(null);

  // Define state variables to manage the visibility of different order statuses
  const [activeStatus, setActiveStatus] = useState(""); // "", "Created", "Confirmed", "Shipped", "Failed", "Cancel"
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  const handleStatusFilter = (status) => {
    setActiveStatus(activeStatus === status ? "" : status);
  };

  const handleDownloadInvoice = async (order) => {
  try {
    // 1️⃣ First, call the API to generate the invoice
    const generateResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}genrate_invoice/${order?.order_no}`,
      {
        method: "GET",
      }
    );

    if (!generateResponse.ok) {
      throw new Error("Failed to generate invoice");
    }

    // Optional: wait a bit to ensure invoice is generated before download
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // 2️⃣ Construct the download URL for the generated invoice
    const invoiceUrl = `https://cyclecircle.blr1.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_INVOICE/${order?.order_no}.pdf`;

    // 3️⃣ Create a temporary link element for download
    const link = document.createElement("a");
    link.href = invoiceUrl;
    link.setAttribute("download", `${order?.order_no}_invoice.pdf`);
    document.body.appendChild(link);

    // 4️⃣ Trigger download
    link.click();

    // 5️⃣ Cleanup
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error generating or downloading invoice:", error);
  }
};


  useEffect(() => {
    const fetchData = async () => {
      if (Object.keys(userDetail).length) {
        handleOpen();
        await dispatch(getAllOrderAsync(userDetail?.UserID));
        handleClose();
      }
    };
    fetchData();
    return () => clearTimeout(2000);
  }, [dispatch, userDetail]);

  // Filter orders based on the entered order code
  useEffect(() => {
    const capitalizedSearchTerm = searchTerm.toUpperCase();

    setFilteredOrders(
      orders.orderResults?.filter((order) =>
        order.order_no.includes(capitalizedSearchTerm)
      ) || []
    );
  }, [orders.orderResults, searchTerm]);

  useEffect(() => {
    if (selectedMonth !== "") {
      const currentDate = new Date();
      const startDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - selectedMonth,
        1
      );

      setFilteredOrders(
        orders.orderResults?.filter((order) => {
          const orderDate = new Date(order.order_date);
          return orderDate >= startDate;
        }) || []
      );
    }
  }, [orders.orderResults, selectedMonth]);

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  const handleViewOrder = (order_ID) => {
    navigate(`/user/orders/view_order/${order_ID}`);
  };

  const toggleShowProducts = (value) => {
    setShowAllProducts(showAllProducts === value ? null : value);
    if (!showAllProducts) {
      const showMoreButton = document.getElementById(
        `show-more-${value}-button`
      );
      showMoreButton?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  // Sort the filteredOrders array by date and time in descending order
  const sortedOrders = [...filteredOrders].sort(
    (a, b) => new Date(b.order_date) - new Date(a.order_date)
  );

  // 🔹 Filter by selected status
  const filterOrders = (order) => {
    const status = order?.orderStatus?.status;
    if (!status) return false;
    if (!activeStatus) return true;
    return status === activeStatus;
  };

  return (
    <div className="my-order-page-container">
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open} // Show backdrop when either open or filtering state is true
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <span className="my-order-total-order">
        My Orders <b>{sortedOrders && sortedOrders.length}</b>
      </span>
      {/* 🔹 FILTER HEADER */}
      <div className="my-order-page-header-container">
        <div className="w-full flex items-center gap-2 sm:gap-4 overflow-x-auto scrollbar-hide py-2 px-1 sm:px-1">
          {[
            {
              label: "Pending",
              value: "Created",
              color: "from-purple-500 to-purple-500",
            },
            {
              label: "Confirmed",
              value: "Success",
              color: "from-green to-green",
            },
            {
              label: "Shipped",
              value: "Shipped",
              color: "from-orange-500 to-orange-500",
            },
            {
              label: "Failed",
              value: "Failed",
              color: "from-gray-500 to-gray-500",
            },
            {
              label: "Cancelled",
              value: "Cancel",
              color: "from-peach to-peach",
            },
          ].map((status) => {
            const isActive = activeStatus === status.value;
            return (
              <button
                key={status.value}
                onClick={() => handleStatusFilter(status.value)}
                className={`relative flex-shrink-0 px-4 py-1.5 sm:px-5 sm:py-1.5 text-sm sm:text-[15px] font-semibold rounded-full transition-all duration-300 border
          ${
            isActive
              ? `text-white border-transparent bg-gradient-to-r ${status.color} shadow-md scale-[1.03]`
              : `text-gray-600 border border-gray-300 hover:border-gray-400 hover:text-gray-800`
          }
        `}
              >
                {status.label}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-[2px] bg-white/70 rounded-full"
                    style={{ transform: "translateX(-50%)" }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="my-order-page-right-header-container">
          <div className="my-order-page-search-div">
            <SearchOutlinedIcon />
            <input
              type="text"
              value={searchTerm}
              placeholder="Enter Order Code"
              className="bg-transparent"
              onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
            />
          </div>
          <div className="my-order-page-monthly-order-div">
            <select name="month" id="month" onChange={handleMonthChange}>
              <option value="">Select Month</option>
              <option value="1">Last 1 Month</option>
              <option value="2">Last 2 Months</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
              <option value="12">Last 1 Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* 🔹 ORDER LIST */}
      {sortedOrders &&
        sortedOrders.map(
          (order, index) =>
            filterOrders(order) && (
              <div className="my-order-page-main-order-by-date-container">
                <div className="my-order-page-date-order-header-div">
                  <div className="my-order-page-date-order-header-left-div">
                    <div className="my-order-page-date-header-left-orderID-div">
                      <span className="left-orderID-heading">Order #</span>
                      <span className="left-orderID-value">
                        {order?.order_no}
                      </span>
                    </div>
                    <div className="my-order-page-date-header-left-date-div">
                      <span className="left-date-heading">Order Date</span>
                      <span className="left-date-value">
                        {new Date(order?.order_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </div>
                    {/* 🔹 STATUS DISPLAY */}
                    <div className="my-order-page-date-header-left-status-div">
                      <span className="left-status-heading">Status</span>
                      <div className="left-status-value-processing">
                        <p
                          className={`${
                            order?.orderStatus?.status === "Success"
                              ? "bg-green"
                              : order?.orderStatus?.status === "Cancel"
                              ? "bg-peach"
                              : order?.orderStatus?.status === "Created"
                              ? "bg-purple-600"
                              : order?.orderStatus?.status === "Shipped"
                              ? "bg-orange-500"
                              : order?.orderStatus?.status === "Failed"
                              ? "bg-gray-600"
                              : "bg-gray-400"
                          } shadow-sm rounded-full w-3 h-3 mr-1`}
                        />

                        <span
                          className={`${
                            order?.orderStatus?.status === "Success"
                              ? "text-green"
                              : order?.orderStatus?.status === "Cancel"
                              ? "text-red-600"
                              : order?.orderStatus?.status === "Created"
                              ? "text-purple-600"
                              : order?.orderStatus?.status === "Shipped"
                              ? "text-orange-500"
                              : order?.orderStatus?.status === "Failed"
                              ? "text-gray-600"
                              : "text-gray-500"
                          }`}
                        >
                          {order?.orderStatus?.status === "Success"
                            ? "Confirmed"
                            : order?.orderStatus?.status === "Created"
                            ? "Pending"
                            : order?.orderStatus?.status}
                        </span>
                      </div>
                    </div>
                    {order?.orderStatus?.status === "Success" && (
                      <div className="my-order-page-date-header-left-date-div">
                        <span className="left-date-heading">ETA</span>
                        <span className="left-date-value">
                          {new Date(
                            new Date(order?.order_date).getTime() +
                              7 * 24 * 60 * 60 * 1000
                          ).toLocaleDateString("en-US", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="my-order-page-date-order-header-right-div">
                    <span className="my-order-page-date-order-header-order-again hidden">
                      Order Again
                    </span>
                    <span
                      className="my-order-page-date-order-header-view-order"
                      onClick={() => handleViewOrder(order?.order_no)}
                    >
                      View Order
                    </span>
                  </div>
                </div>
                {order?.orderItems &&
                  order?.orderItems?.map((product, index) => (
                    <div
                      key={product?.item_no_SKU}
                      className={`my-order-page-date-order-each-order-div ${
                        index > 2 && !showAllProducts ? "hidden" : ""
                      }`}
                    >
                      <div className="my-order-page-date-order-each-order-left-div">
                        <LazyLoadImage
                          src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_PRODUCTS_IMAGE/${product.prodimage}`}
                          alt=""
                          className="my-order-page-date-order-each-order-left-img"
                          effect="blur"
                          wrapperProps={{
                            // If you need to, you can tweak the effect transition using the wrapper style.
                            style: { transitionDelay: "1s" },
                          }}
                        />
                        <div className="my-order-page-date-order-each-order-left-detail-div">
                          <LazyLoadImage
                            src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_MANUFACTURE_IMAGE/${
                              product && product?.manufacture_images
                            }`}
                            alt=""
                            className="my-order-page-date-order-each-order-left-detail-brands-logo"
                            effect="blur"
                            wrapperProps={{
                              // If you need to, you can tweak the effect transition using the wrapper style.
                              style: { transitionDelay: "1s" },
                            }}
                          />
                          <span className="my-order-page-date-order-each-order-left-product-name">
                            {product?.prod_name}
                          </span>
                          <span className="my-order-page-date-order-each-order-left-product-tech">
                            {product?.prodTech &&
                              product?.prodTech
                                .slice(0, 1)
                                .map((tech, index) => <span>{tech} |</span>)}
                          </span>
                        </div>
                      </div>
                      <div className="my-order-page-date-order-each-order-left-product-variant-qty-div">
                        <span className="my-order-page-date-order-each-order-left-product-qty">
                          Qty: <b>{product?.quantity}</b>
                        </span>
                        <div className="my-order-page-date-order-each-order-left-product-variant-detail">
                          {product?.size_name && (
                            <span className="my-order-page-date-order-each-order-left-product-variant-size-div">
                              Size:{" "}
                              <b>{product?.size_name && product?.size_name}</b>
                            </span>
                          )}
                          {product?.colour_name && (
                            <span className="my-order-page-date-order-each-order-left-product-variant-color-div">
                              Color:{" "}
                              <b>
                                {product?.colour_name && product?.colour_name}
                              </b>
                            </span>
                          )}
                          {product?.variant_name && (
                            <span className="my-order-page-date-order-each-order-left-product-variant-variant-div">
                              Variant:{" "}
                              <b>
                                {product?.variant_name && product?.variant_name}
                              </b>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="my-order-page-date-order-each-order-right-div hidden">
                        <span className="my-order-page-date-order-each-order-right-write-review">
                          Write a review
                        </span>
                        <span className="my-order-page-date-order-each-order-right-buy-again-it">
                          Return this Item
                        </span>
                      </div>
                    </div>
                  ))}
                {!showAllProducts && order.orderItems.length > 3 && (
                  <span
                    id={`show-more-${order.orderStatus}-button`}
                    className="show-more-button"
                    onClick={() => toggleShowProducts(order.orderStatus)}
                  >
                    {order.orderItems.length - 3} more items{" "}
                    <KeyboardArrowDownIcon />
                  </span>
                )}
                {/* Show less button */}
                {showAllProducts === order.orderStatus &&
                  order.orderItems.length > 3 && (
                    <span
                      id={`show-less-${order.orderStatus}-button`}
                      className="show-less-button"
                      onClick={() => toggleShowProducts(order.orderStatus)}
                    >
                      Show less <KeyboardArrowUpIcon />
                    </span>
                  )}
                <div className="my-order-page-main-order-by-date-total-price-invoice-div">
                  <div className="my-order-page-main-order-by-date-total-price-div">
                    <span className="my-order-total-price-key">
                      Order Total:
                    </span>
                    <span className="my-order-total-price-value">
                      ₹
                      {order?.TotalAmount ? formatWithCommasWithDecimal(
                        order?.TotalAmount && parseInt(order?.TotalAmount)
                      ) : 0}{" "}
                    </span>
                  </div>

                  <div
                    className="my-order-page-main-order-invoice-div"
                    onClick={() => handleDownloadInvoice(order)}
                  >
                    <ReceiptIcon />
                    <span>Invoice</span>
                  </div>
                </div>
              </div>
            )
        )}
    </div>
  );
}

export default MyOrder;
